import { useState } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";

import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Search, Download, Calendar, Mail, FileCheck, Globe } from "lucide-react";
import { Spin, DatePicker, ConfigProvider, Select } from "antd";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";
import { RemittanceDrawer } from "./common/RemittanceDrawer";
import { RemittanceTable } from "./common/RemittanceTable";
import { FetchReportByRemittance_api } from "../../Api/Reports/fetch_report_by_remittance";

const { RangePicker } = DatePicker;
const { Option } = Select;

export const ReportByRemittance = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.REPORT_BY_REMITTANCE,
    ["admin", "cashier", "manager"],
  );

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const fetchReport = async () => {
    if (!startDate && !endDate && !searchKeyword) {
      alert("Please select dates or enter a search keyword");
      return;
    }
    setLoading(true);
    const response = await FetchReportByRemittance_api(
      startDate,
      endDate,
      searchKeyword,
    );
    if (response.success) {
      setReportData(response.data);
    } else {
      alert(response.msg || "Failed to fetch report");
    }
    setLoading(false);
  };

  const toNumber = (value: any): number =>
    Number(String(value ?? "0").replace(/,/g, "")) || 0;

  const calculateTotal = () => {
    const total = reportData.reduce((acc, curr) => {
      const amounts =
        curr.amount?.split(",").map((a: string) => toNumber(a)) || [];
      return acc + amounts.reduce((s: number, a: number) => s + a, 0);
    }, 0);

    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(total);
  };

  const handleDownloadExcel = async () => {
    if (!startDate || !endDate) {
      alert("Select a date range first");
      return;
    }
    const allData = reportData;

    const headers = [
      "Inquiry No",
      "Transfer ID",
      "Customer",
      "Vehicle",
      "Payments",
      "Total Amount",
    ];

    const data = allData.map((r) => [
      r.inquiryNumber,
      r.transfer_id,
      `${r.name} (${r.mobile})`,
      `${r.v_modal} - ${r.v_type}`,
      r.amount
        .split(",")
        .map((a: string) =>
          toNumber(a).toLocaleString("en-LK", { minimumFractionDigits: 2 }),
        )
        .join(" + "),
      toNumber(r.totalamount),
    ]);

    const totalAmount = allData.reduce((acc, r) => {
      const amounts = r.amount.split(",").map(toNumber);
      return acc + amounts.reduce((s, a) => s + a, 0);
    }, 0);

    downloadExcel(
      `Remittance Report (${startDate} to ${endDate})`,
      headers,
      data,
      `Remittance_Report_${startDate}_${endDate}`,
      new Intl.NumberFormat("en-LK", {
        style: "currency",
        currency: "LKR",
        minimumFractionDigits: 2,
      }).format(totalAmount),
      undefined,
      undefined,
      `${allData.length} Transfers`,
    );
  };

  if (permissionLoading) return <LoadingScreen messageQueue="Loading..." />;
  if (!hasPermission) return <NoAccessScreen />;

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Remittance Report</h1>
        <div className="flex gap-3">
          <Button 
            onClick={handleDownloadExcel} 
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Start Date
          </label>
          <DatePicker 
            className="w-full h-[38px]"
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => setStartDate(date ? date.format("YYYY-MM-DD") : "")}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            End Date
          </label>
          <DatePicker 
            className="w-full h-[38px]"
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => setEndDate(date ? date.format("YYYY-MM-DD") : "")}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Identifiers
          </label>
          <Input
            placeholder="Inquiry / Transfer ID"
            className="h-[38px]"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </div>

        <Button
          onClick={fetchReport}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white h-[38px]"
        >
          {loading ? <Spin size="small" /> : <Search className="w-4 h-4 mr-2" />}
          <span>Search</span>
        </Button>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL REMITTANCE VOLUME</p>
                <p className="text-2xl font-bold">{calculateTotal()}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL TRANSFERS</p>
                <p className="text-2xl font-bold">{reportData.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col p-6">
            <RemittanceTable
              loading={loading}
              reportData={reportData}
              setSelectedRow={setSelectedRow}
              setDrawerOpen={setDrawerOpen}
            />
          </div>
        </div>
      )}

      {reportData.length === 0 && !loading && (startDate || endDate || searchKeyword) && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No remittance activity recorded for this criteria.</p>
          </div>
        </div>
      )}
      {reportData.length === 0 && !loading && !startDate && !endDate && !searchKeyword && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Define search parameters to view remittance insights.</p>
          </div>
        </div>
      )}


      <RemittanceDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data={selectedRow}
      />
    </div>
  );
};
